import { useState, useMemo, useCallback } from 'react';
import { Search, MessageSquareQuote } from 'lucide-react';
import { VERBATIM_DATA } from '../../data/verbatimData';
import type { VerbatimRespondent } from '../../data/verbatimData';
import {
  BehavioralFilterBar,
  type SegmentFilter, type PriceFilter, type ObjectionFilter,
  type SpendFilter, type GoalFilter, type AgeFilter,
} from './BehavioralFilterBar';
import { SpotlightCards } from './SpotlightCards';
import { GroupProfileCard } from './GroupProfileCard';
import { SaidVsDidPanel } from './SaidVsDidPanel';
import { VerbatimWall } from './VerbatimWall';
import { InfoButton } from '../common/Tooltip';

function matchesSearch(r: VerbatimRespondent, term: string): boolean {
  const lower = term.toLowerCase();
  return (
    r.goals.toLowerCase().includes(lower) ||
    r.wtpFactors.toLowerCase().includes(lower) ||
    r.priceObjection.toLowerCase().includes(lower) ||
    r.brandPerception.toLowerCase().includes(lower) ||
    r.otherBrand.toLowerCase().includes(lower) ||
    r.otherGoal.toLowerCase().includes(lower) ||
    r.primaryGoal.toLowerCase().includes(lower)
  );
}

function getFilterDescription(
  seg: SegmentFilter, price: PriceFilter, obj: ObjectionFilter,
  spend: SpendFilter, goal: GoalFilter, age: AgeFilter,
): string {
  const parts: string[] = [];
  if (seg === 'always-none') parts.push('rejected all products');
  else if (seg === 'never-none') parts.push('always chose a product');
  else if (seg === 'selective') parts.push('mixed choosers');
  if (price === 'low') parts.push('budget-oriented');
  else if (price === 'mid') parts.push('mid-price preference');
  else if (price === 'high') parts.push('premium preference');
  if (obj === 'has') parts.push('with price objection');
  else if (obj === 'none') parts.push('no price objection');
  if (spend === 'nothing') parts.push('spends nothing');
  else if (spend === 'under50') parts.push('spends < $50/mo');
  else if (spend === '50plus') parts.push('spends $50+/mo');
  if (goal !== 'all') parts.push(goal);
  if (age === '18-34') parts.push('age 18–34');
  else if (age === '35-49') parts.push('age 35–49');
  else if (age === '50plus') parts.push('age 50+');
  return parts.join(', ');
}

const GOAL_KEYWORDS: Record<GoalFilter, string[]> = {
  all: [],
  weight: ['weight', 'body composition', 'losing'],
  health: ['general health', 'longevity'],
  strength: ['strength', 'athletic', 'muscle'],
  nutrition: ['nutrition', 'eating'],
  activity: ['activity', 'movement'],
  stress: ['stress', 'mental'],
};

export function VoiceOfMarketTab() {
  const [segmentFilter, setSegmentFilter] = useState<SegmentFilter>('all');
  const [priceFilter, setPriceFilter] = useState<PriceFilter>('all');
  const [objectionFilter, setObjectionFilter] = useState<ObjectionFilter>('all');
  const [spendFilter, setSpendFilter] = useState<SpendFilter>('all');
  const [goalFilter, setGoalFilter] = useState<GoalFilter>('all');
  const [ageFilter, setAgeFilter] = useState<AgeFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const isFiltered = segmentFilter !== 'all' || priceFilter !== 'all' || objectionFilter !== 'all'
    || spendFilter !== 'all' || goalFilter !== 'all' || ageFilter !== 'all' || searchTerm.trim() !== '';

  const applyFilters = useCallback((list: VerbatimRespondent[]) => {
    let filtered = list;

    if (segmentFilter !== 'all') {
      filtered = filtered.filter(r => r.segment === segmentFilter);
    }

    if (priceFilter !== 'all') {
      filtered = filtered.filter(r => {
        if (r.segment === 'always-none') return priceFilter === 'low';
        const total = Object.values(r.priceDistribution).reduce((a, b) => a + b, 0);
        if (total === 0) return false;
        if (priceFilter === 'low') return (r.priceDistribution['499'] || 0) / total > 0.5;
        if (priceFilter === 'mid') {
          const mid = (r.priceDistribution['999'] || 0) + (r.priceDistribution['1499'] || 0) + (r.priceDistribution['1699'] || 0) + (r.priceDistribution['1899'] || 0);
          return mid / total > 0.5;
        }
        if (priceFilter === 'high') {
          const high = (r.priceDistribution['1499'] || 0) + (r.priceDistribution['1699'] || 0) + (r.priceDistribution['1899'] || 0);
          return high / total > 0.5;
        }
        return true;
      });
    }

    if (objectionFilter === 'has') {
      filtered = filtered.filter(r => r.priceObjection.length > 0);
    } else if (objectionFilter === 'none') {
      filtered = filtered.filter(r => r.priceObjection.length === 0);
    }

    if (spendFilter === 'nothing') {
      filtered = filtered.filter(r => r.monthlySpend.toLowerCase().includes('nothing'));
    } else if (spendFilter === 'under50') {
      filtered = filtered.filter(r => /less than \$25|\$25/i.test(r.monthlySpend));
    } else if (spendFilter === '50plus') {
      filtered = filtered.filter(r => /\$50|\$100|\$200/i.test(r.monthlySpend));
    }

    if (goalFilter !== 'all') {
      const keywords = GOAL_KEYWORDS[goalFilter];
      filtered = filtered.filter(r =>
        keywords.some(k => r.primaryGoal.toLowerCase().includes(k))
      );
    }

    if (ageFilter === '18-34') {
      filtered = filtered.filter(r => r.age >= 18 && r.age <= 34);
    } else if (ageFilter === '35-49') {
      filtered = filtered.filter(r => r.age >= 35 && r.age <= 49);
    } else if (ageFilter === '50plus') {
      filtered = filtered.filter(r => r.age >= 50);
    }

    return filtered;
  }, [segmentFilter, priceFilter, objectionFilter, spendFilter, goalFilter, ageFilter]);

  const filtered = useMemo(() => {
    let list = applyFilters(VERBATIM_DATA);
    if (searchTerm.trim()) {
      list = list.filter(r => matchesSearch(r, searchTerm.trim()));
    }
    return list;
  }, [applyFilters, searchTerm]);

  const filterDesc = getFilterDescription(segmentFilter, priceFilter, objectionFilter, spendFilter, goalFilter, ageFilter);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 mb-1">
        <MessageSquareQuote size={18} className="text-chart-teal" />
        <h2 className="text-lg font-bold text-primary">Voice of Market</h2>
        <InfoButton helpKey="voiceOfMarket" size={14} />
      </div>
      <p className="text-sm text-muted -mt-2 mb-4">
        Open-ended survey responses from {VERBATIM_DATA.length.toLocaleString()} conjoint respondents, linked to their actual choice behavior and demographics.
      </p>

      <BehavioralFilterBar
        segment={segmentFilter} price={priceFilter} objection={objectionFilter}
        spend={spendFilter} goal={goalFilter} ageRange={ageFilter}
        onSegmentChange={setSegmentFilter} onPriceChange={setPriceFilter} onObjectionChange={setObjectionFilter}
        onSpendChange={setSpendFilter} onGoalChange={setGoalFilter} onAgeChange={setAgeFilter}
        totalCount={VERBATIM_DATA.length} filteredCount={filtered.length} filterDescription={filterDesc}
      />

      <GroupProfileCard respondents={filtered} isFiltered={isFiltered} />

      <SpotlightCards data={VERBATIM_DATA} />

      <SaidVsDidPanel respondents={filtered} />

      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder='Search verbatims… (e.g. "DNA", "expensive", "ring")'
          className="w-full pl-9 pr-4 py-2 bg-surface border border-border rounded-lg text-sm text-primary placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-chart-teal/30"
        />
      </div>

      {filtered.length > 0 ? (
        <VerbatimWall respondents={filtered} />
      ) : (
        <div className="text-center py-12 text-muted text-sm">
          No respondents match the current filters{searchTerm && ` and search term "${searchTerm}"`}.
        </div>
      )}
    </div>
  );
}
